import { getCookie, setCookie } from "../../utils/cookies";
import { isSharedCookieHost, SHARED_COOKIE_DOMAIN } from "../../../common/dmnCookies";
import {
  type ConsentCategories,
  type ConsentChangeCallback,
  type CookieConsentProvider,
  NO_CONSENT_CATEGORIES,
} from "./types";

/**
 * First-party cookie the main `designmynight.com` site writes to record a
 * consent decision. Scoped to the apex domain so it is readable from any
 * `*.designmynight.com` subdomain.
 */
export const CONSENT_COOKIE_NAME = "consent";

/**
 * Apex domain the shared consent cookie is scoped to.
 *
 * Re-exported from `common` so the server's DesignMyNight session cookie and
 * this consent cookie cannot drift apart.
 */
export { SHARED_COOKIE_DOMAIN };

/**
 * Whether `hostname` is `designmynight.com` or one of its subdomains, i.e. a
 * context where the shared `consent` cookie can be trusted and the checkout is
 * responsible for the visitor's consent decision.
 */
export function isSharedConsentHost(hostname: string | undefined): boolean {
  return isSharedCookieHost(hostname);
}

/**
 * Termly website whose banner configuration is used. The default is the live
 * `designmynight.com` website, so the banner is visually and functionally
 * identical to the main site (see `resources/views/layout.blade.php`).
 *
 * Overridden per environment via `VITE_TERMLY_WEBSITE_ID`, so a non-production
 * environment can point at a Termly website configured for opt-in consent in
 * every region. Termly decides banner behaviour from the visitor's IP region
 * and the website's per-region settings, so with the live website a visitor
 * outside the EU is never shown a banner and cannot be asked to decide — see
 * `docs/consent.md#testing-the-banner-outside-the-eu`.
 */
export const DEFAULT_TERMLY_WEBSITE_ID = "a33cdef5-9017-4fdd-bb72-f4e4e47977e1";

/** Resource-blocker script URL for a Termly website. */
export function buildResourceBlockerUrl(websiteId: string): string {
  return `https://app.termly.io/resource-blocker/${websiteId}?autoBlock=off`;
}

/** Resource-blocker script for the default (live) Termly website. */
export const TERMLY_RESOURCE_BLOCKER_URL = buildResourceBlockerUrl(DEFAULT_TERMLY_WEBSITE_ID);

/**
 * Origin of the main site whose consent decisions this subdomain inherits.
 * Overridden per environment via `VITE_TERMLY_MASTER_CONSENTS_ORIGIN`.
 */
export const DEFAULT_MASTER_CONSENTS_ORIGIN = "https://www.designmynight.com";

/** Path of the parent-site page hosting Termly's `consent-sync.min.js`. */
export const TERMLY_CONSENT_SYNC_PATH = "/termly-consent-sync.html";

/**
 * Resource-blocker URL telling Termly to inherit consent from the main site.
 */
export function buildTermlyScriptUrl(
  masterConsentsOrigin: string,
  websiteId: string = DEFAULT_TERMLY_WEBSITE_ID,
): string {
  return `${buildResourceBlockerUrl(websiteId)}&masterConsentsOrigin=${masterConsentsOrigin}`;
}

/**
 * Parent-site page loaded in a hidden iframe so Termly can replay a consent
 * decision made on the main site into this subdomain (parent -> child only).
 */
export function buildConsentSyncUrl(masterConsentsOrigin: string): string {
  return `${masterConsentsOrigin}${TERMLY_CONSENT_SYNC_PATH}`;
}

/** Resource-blocker script for the default main-site origin. */
export const TERMLY_SCRIPT_URL = buildTermlyScriptUrl(DEFAULT_MASTER_CONSENTS_ORIGIN);

/** Consent-sync page for the default main-site origin. */
export const TERMLY_CONSENT_SYNC_URL = buildConsentSyncUrl(DEFAULT_MASTER_CONSENTS_ORIGIN);

/** Cookie values used by the main site: `'1'` accepted, `'0'` declined. */
export enum ConsentValue {
  DECLINED = "0",
  ACCEPTED = "1",
}

/**
 * Event Termly dispatches on `document` whenever the visitor grants or changes
 * consent. Its `detail` is the list of granted category ids, e.g.
 * `["essential", "analytics", "performance"]`. Termly does not emit any
 * `termly-consent-*` events, so this is the only in-session signal available.
 *
 * Termly also dispatches it while initialising, carrying the region's default
 * categories rather than anything the visitor chose, so the payload is never
 * trusted — see {@link TermlyCookieConsentProvider.handleTermlyEvent}.
 */
const TERMLY_CONSENT_EVENT = "termlyConsent";

/** Termly mirrors its own state into `localStorage` under this key. */
const TERMLY_CACHE_KEY = "TERMLY_API_CACHE";

/**
 * Termly's own category ids, mapped to the provider-agnostic
 * {@link ConsentCategories} names.
 */
const TERMLY_CATEGORY_NAMES = {
  essential: "essential",
  analytics: "analytics",
  performance: "performance",
  advertising: "advertising",
  socialNetworking: "social_networking",
  unclassified: "unclassified",
} as const satisfies Record<keyof ConsentCategories, string>;

/**
 * Categories inferred from the shared apex cookie, which records only that a
 * decision was made on the main site and not what the visitor chose per
 * purpose. Only the purposes the checkout itself relies on are inferred;
 * advertising and the remaining purposes stay refused until Termly's own
 * record — replayed into this subdomain by the consent-sync frame — says
 * otherwise.
 */
const SHARED_COOKIE_CATEGORIES: ConsentCategories = {
  ...NO_CONSENT_CATEGORIES,
  analytics: true,
  performance: true,
};

/**
 * Flag Termly's script reads to suppress its own Google consent mode signals.
 *
 * Guest Checkout owns those signals (see `src/frontend/analytics/consent.ts`)
 * so they follow the decision recorded here, rather than Termly's per-region
 * dashboard defaults, and so `default` is always sent immediately before a
 * Google library is injected — the only point at which it takes effect.
 */
const TERMLY_DISABLE_GCM_FLAG = "TERMLY_FORCE_DISABLE_GCM";

/**
 * Delays, in milliseconds, at which Termly's consent record is looked for again
 * after an event arrived without one. Termly dispatches the event and writes
 * its consent record independently, so a genuine decision can arrive a tick
 * before it is recorded.
 */
const DECISION_RECHECK_DELAYS_MS = [50, 200, 500] as const;

/** Globals Termly's script defines once it has run. */
interface TermlyWindow {
  /**
   * Opens Termly's Preference Centre, where every purpose can be granted or
   * withdrawn. It is the same modal the banner's "Preferences" button opens,
   * and is undefined until Termly's script has run.
   */
  displayPreferenceModal?: () => void;
}

/**
 * Delays, in milliseconds, at which Termly's Preference Centre is opened again
 * after a request arrived before its script had defined the function — the
 * first request of a returning visit injects the script and cannot open the
 * modal synchronously.
 */
const PREFERENCE_MODAL_RETRY_DELAYS_MS = [50, 200, 500, 1000] as const;

const CONSENT_COOKIE_DAYS = 365;

export interface TermlyCookieConsentProviderOptions {
  readonly scriptUrl?: string;
  readonly cookieName?: string;
  readonly cookieDomain?: string;
  /** Apex host under which the shared cookie is trusted, e.g. `designmynight.com`. */
  readonly sharedHost?: string;
  /** Origin of the main site whose consent decisions are inherited. */
  readonly masterConsentsOrigin?: string;
  /**
   * Whether the shared cookie is written with the `Secure` flag. Defaults to
   * whether the current page is served over HTTPS, since a `Secure` cookie is
   * discarded on an insecure origin such as local development.
   */
  readonly secure?: boolean;
  readonly disabled?: boolean;
  /** Termly website whose banner configuration is used. */
  readonly websiteId?: string;
}

/**
 * Termly-backed {@link CookieConsentProvider}.
 *
 * On a `*.designmynight.com` subdomain it reuses the shared first-party
 * `consent` cookie the main site writes, so a decision made on the main site
 * suppresses the banner here (and vice versa). When embedded on a third-party
 * venue domain — where the shared cookie cannot be trusted — it falls back to
 * letting Termly manage consent independently for that domain.
 */
export class TermlyCookieConsentProvider implements CookieConsentProvider {
  private readonly scriptUrl: string;
  private readonly cookieName: string;
  private readonly cookieDomain: string;
  private readonly sharedHost: string;
  private readonly consentSyncUrl: string;
  private readonly secure: boolean;
  private readonly disabled: boolean;

  private listening = false;
  private recheckTimer: ReturnType<typeof setTimeout> | null = null;
  private preferenceModalTimer: ReturnType<typeof setTimeout> | null = null;
  /** Last decision published to subscribers, so a record still showing it is known to be stale. */
  private publishedCategories: ConsentCategories | null = null;
  private readonly subscribers = new Set<ConsentChangeCallback>();

  constructor(options: TermlyCookieConsentProviderOptions = {}) {
    const masterConsentsOrigin = options.masterConsentsOrigin ?? DEFAULT_MASTER_CONSENTS_ORIGIN;
    this.scriptUrl = options.scriptUrl ?? buildTermlyScriptUrl(masterConsentsOrigin, options.websiteId);
    this.consentSyncUrl = buildConsentSyncUrl(masterConsentsOrigin);
    this.cookieName = options.cookieName ?? CONSENT_COOKIE_NAME;
    this.cookieDomain = options.cookieDomain ?? SHARED_COOKIE_DOMAIN;
    this.sharedHost = options.sharedHost ?? SHARED_COOKIE_DOMAIN;
    this.secure = options.secure ?? (typeof window !== "undefined" && window.location.protocol === "https:");
    this.disabled = options.disabled ?? false;

    this.mirrorRecordedDecision();
  }

  /**
   * Mirror a Termly decision already recorded on this subdomain (e.g. inherited
   * from the main site or made on a previous visit) into the shared cookie, so
   * the main site can read it without waiting for a new consent event.
   *
   * Only written when the shared cookie is absent: Termly's record here is
   * per-subdomain and is only refreshed from the main site while no decision
   * exists, so it can be older than a decision since made on the main site.
   * Overwriting the cookie would revert that newer decision.
   */
  private mirrorRecordedDecision(): void {
    if (this.disabled || !this.isSharedHost()) return;
    if (getCookie(this.cookieName) !== null) return;

    const recorded = this.readTermlyCategories();
    if (recorded === null) return;

    this.writeSharedCookie(hasMeasurementConsent(recorded));
  }

  hasResponded(): boolean {
    if (this.disabled) return true;
    return this.hasAnyRecordedDecision();
  }

  hasConsented(): boolean {
    const categories = this.getCategories();
    return categories !== null && hasMeasurementConsent(categories);
  }

  /**
   * The recorded decision by purpose.
   *
   * Termly's own record is preferred: it is the authoritative, per-category
   * copy of what the visitor chose. The shared apex cookie is only consulted
   * where it can be trusted and Termly has recorded nothing on this domain
   * yet, and yields the coarse {@link SHARED_COOKIE_CATEGORIES} inference.
   */
  getCategories(): ConsentCategories | null {
    if (this.disabled) return null;

    const recorded = this.readTermlyCategories();
    if (recorded !== null) return recorded;

    if (this.isSharedHost()) {
      const cookie = getCookie(this.cookieName);
      if (cookie !== null) {
        return cookie === ConsentValue.ACCEPTED ? SHARED_COOKIE_CATEGORIES : NO_CONSENT_CATEGORIES;
      }
    }

    return null;
  }

  showBanner(): void {
    if (!this.disabled) this.injectScript();
  }

  canShowPreferences(): boolean {
    return !this.disabled;
  }

  /**
   * Open Termly's Preference Centre, injecting its script first when nothing
   * has been rendered this visit.
   *
   * {@link showBanner} is the only other injection point and is skipped once a
   * decision has been recorded, so on a returning visit Termly is absent and
   * the modal has to be requested again once its script has run. Termly also
   * hides its own floating preferences button wherever the region is configured
   * for implied consent, which leaves this the only way for a visitor to revisit
   * their decision.
   */
  showPreferences(): void {
    if (this.disabled || typeof window === "undefined") return;

    // A retry from an earlier press would otherwise still fire and reopen the
    // modal once Termly has loaded.
    this.cancelPreferenceModalRetry();

    this.injectScript();
    if (this.openPreferenceModal()) return;

    this.schedulePreferenceModal();
  }

  onConsentChange(callback: ConsentChangeCallback): () => void {
    if (this.disabled) return () => undefined;
    this.subscribers.add(callback);
    this.startListening();
    return () => {
      this.subscribers.delete(callback);
      if (this.subscribers.size === 0) {
        this.stopListening();
      }
    };
  }

  /**
   * Whether a consent decision has actually been recorded, either by the main
   * site in the shared cookie or by Termly itself. Termly's in-page state
   * exists from initialisation onwards, so only a persisted record proves the
   * visitor decided anything.
   */
  private hasAnyRecordedDecision(): boolean {
    return this.getCategories() !== null;
  }

  /**
   * Whether the current host is `designmynight.com` or one of its subdomains,
   * i.e. a context where the shared `consent` cookie can be trusted.
   */
  private isSharedHost(): boolean {
    if (typeof window === "undefined") return false;
    const host = window.location?.hostname;
    if (!host) return false;
    return host === this.sharedHost || host.endsWith(`.${this.sharedHost}`);
  }

  /**
   * Read Termly's own consent state from `localStorage`. This is only valid on
   * the current domain (localStorage is not shared across subdomains).
   *
   * @returns the recorded categories, or `null` when nothing is recorded.
   */
  private readTermlyCategories(): ConsentCategories | null {
    if (typeof localStorage === "undefined") return null;
    try {
      const raw = localStorage.getItem(TERMLY_CACHE_KEY);
      if (!raw) return null;
      const cache = JSON.parse(raw);
      const value = cache?.TERMLY_COOKIE_CONSENT?.value;
      if (!value) return null;
      return toConsentCategories(value);
    } catch {
      return null;
    }
  }

  private startListening(): void {
    if (this.listening || typeof document === "undefined") return;
    this.listening = true;
    document.addEventListener(TERMLY_CONSENT_EVENT, this.handleTermlyEvent);
  }

  private stopListening(): void {
    if (!this.listening || typeof document === "undefined") return;
    this.listening = false;
    this.cancelDecisionRecheck();
    document.removeEventListener(TERMLY_CONSENT_EVENT, this.handleTermlyEvent);
  }

  /**
   * Applies a Termly consent event, but only once Termly has recorded a
   * decision, and always from that record rather than the event payload.
   *
   * Termly emits the same event with the region's default categories while it
   * initialises, before the visitor has chosen anything. Acting on that would
   * start non-essential tracking, dismiss the banner and persist a consent
   * decision the visitor never made. Its own record is the only signal that
   * distinguishes the two, and is the authoritative copy of what the visitor
   * chose, so an event without one is treated as the default-state broadcast it
   * is and only re-examined shortly afterwards, in case the record is still
   * being written.
   *
   * The shared cookie deliberately does not satisfy this check: it records what
   * the visitor decided on the main site, which says nothing about whether the
   * event in hand is a decision. Trusting it would let an initialisation event
   * overwrite a decline with the region's defaults.
   *
   * A record still showing what was last published is stale for the same
   * reason, so a visitor changing their mind mid-session is re-checked rather
   * than republished as the decision they have just replaced.
   */
  private readonly handleTermlyEvent = (event: Event): void => {
    const eventCategories = categoriesFromTermlyEvent(event);
    const decision = this.currentDecision(eventCategories);
    if (decision === null) {
      this.restartDecisionRecheck(eventCategories);
      return;
    }

    this.publish(decision);
  };

  /**
   * Termly's recorded decision when it is newer than the one already published,
   * otherwise `null` — nothing has been recorded, or the record has yet to catch
   * up with the event in hand.
   */
  private currentDecision(eventCategories: ConsentCategories | null = null): ConsentCategories | null {
    const categories = this.readTermlyCategories();
    if (categories === null || (eventCategories !== null && !sameCategories(categories, eventCategories))) return null;
    return sameCategories(categories, this.publishedCategories) ? null : categories;
  }

  /** Mirrors the decision to the shared cookie and notifies subscribers. */
  private publish(categories: ConsentCategories): void {
    this.cancelDecisionRecheck();
    this.publishedCategories = categories;

    const consented = hasMeasurementConsent(categories);

    if (this.isSharedHost()) {
      this.writeSharedCookie(consented);
    }

    for (const subscriber of this.subscribers) {
      subscriber(consented, categories);
    }
  }

  /**
   * Starts the re-check window afresh, so an event arriving while an earlier
   * window is still running is given its own full set of attempts rather than
   * being swallowed by it.
   */
  private restartDecisionRecheck(eventCategories: ConsentCategories | null): void {
    this.cancelDecisionRecheck();
    this.scheduleDecisionRecheck(eventCategories);
  }

  /**
   * Looks for Termly's consent record again shortly after an event arrived
   * ahead of it, so a decision recorded a moment later is still honoured.
   */
  private scheduleDecisionRecheck(eventCategories: ConsentCategories | null, attempt = 0): void {
    if (attempt >= DECISION_RECHECK_DELAYS_MS.length) return;

    this.recheckTimer = setTimeout(() => {
      this.recheckTimer = null;
      if (!this.listening) return;

      const isFinalAttempt = attempt === DECISION_RECHECK_DELAYS_MS.length - 1;
      const decision = this.currentDecision(isFinalAttempt ? null : eventCategories);
      if (decision !== null) {
        this.publish(decision);
        return;
      }

      this.scheduleDecisionRecheck(eventCategories, attempt + 1);
    }, DECISION_RECHECK_DELAYS_MS[attempt]);
  }

  private cancelDecisionRecheck(): void {
    if (this.recheckTimer === null) return;
    clearTimeout(this.recheckTimer);
    this.recheckTimer = null;
  }

  /**
   * Calls Termly's Preference Centre function.
   *
   * @returns whether the function existed, i.e. whether Termly has loaded.
   */
  private openPreferenceModal(): boolean {
    const open = (window as Window & TermlyWindow).displayPreferenceModal;
    if (typeof open !== "function") return false;

    open();
    return true;
  }

  /** Retries opening the Preference Centre while Termly's script loads. */
  private schedulePreferenceModal(attempt = 0): void {
    if (attempt >= PREFERENCE_MODAL_RETRY_DELAYS_MS.length) return;

    this.cancelPreferenceModalRetry();
    this.preferenceModalTimer = setTimeout(() => {
      this.preferenceModalTimer = null;
      if (this.openPreferenceModal()) return;

      this.schedulePreferenceModal(attempt + 1);
    }, PREFERENCE_MODAL_RETRY_DELAYS_MS[attempt]);
  }

  private cancelPreferenceModalRetry(): void {
    if (this.preferenceModalTimer === null) return;
    clearTimeout(this.preferenceModalTimer);
    this.preferenceModalTimer = null;
  }

  /** Mirror the decision back into the shared cookie so subdomains stay in sync. */
  private writeSharedCookie(consented: boolean): void {
    setCookie(this.cookieName, consented ? ConsentValue.ACCEPTED : ConsentValue.DECLINED, {
      domain: this.cookieDomain,
      path: "/",
      days: CONSENT_COOKIE_DAYS,
      secure: this.secure,
    });
  }

  private injectScript(): void {
    if (typeof document === "undefined") return;

    this.disableTermlyGoogleConsentMode();
    this.injectConsentSyncFrame();

    if (document.querySelector(`script[src="${this.scriptUrl}"]`)) return;

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = this.scriptUrl;
    document.head.appendChild(script);
  }

  /**
   * Add the hidden consent-sync iframe that mirrors the parent site's decision
   * into this subdomain. Safe to call repeatedly: it inserts at most one frame.
   */
  private injectConsentSyncFrame(): void {
    if (document.querySelector(`iframe[src="${this.consentSyncUrl}"]`)) return;

    const frame = document.createElement("iframe");
    frame.src = this.consentSyncUrl;
    frame.title = "Termly consent sync";
    frame.setAttribute("aria-hidden", "true");
    frame.style.display = "none";
    document.body.appendChild(frame);
  }

  /**
   * Claim ownership of the Google consent mode signal before Termly's script
   * runs, leaving a single writer for it. Termly would otherwise send its
   * dashboard defaults, which are region dependent and grant every purpose in
   * the EU configuration, ahead of any decision.
   */
  private disableTermlyGoogleConsentMode(): void {
    if (typeof window === "undefined") return;
    (window as unknown as Record<string, unknown>)[TERMLY_DISABLE_GCM_FLAG] = true;
  }
}

/**
 * Whether the visitor granted a purpose that permits measurement — the coarse
 * signal consumers such as telemetry rely on. Advertising alone does not
 * qualify: it permits targeting, not analytics.
 */
function hasMeasurementConsent(categories: ConsentCategories): boolean {
  return categories.analytics || categories.performance;
}

/** Translate Termly's recorded category map into {@link ConsentCategories}. */
function toConsentCategories(value: Record<string, unknown>): ConsentCategories {
  // Essential storage needs no consent, so Termly does not always list it.
  return {
    essential: true,
    analytics: value[TERMLY_CATEGORY_NAMES.analytics] === true,
    performance: value[TERMLY_CATEGORY_NAMES.performance] === true,
    advertising: value[TERMLY_CATEGORY_NAMES.advertising] === true,
    socialNetworking: value[TERMLY_CATEGORY_NAMES.socialNetworking] === true,
    unclassified: value[TERMLY_CATEGORY_NAMES.unclassified] === true,
  };
}

function categoriesFromTermlyEvent(event: Event): ConsentCategories | null {
  if (!(event instanceof CustomEvent) || !Array.isArray(event.detail)) return null;
  const granted = Object.fromEntries(
    event.detail
      .filter((category): category is string => typeof category === "string")
      .map((category) => [category, true]),
  );
  return toConsentCategories(granted);
}

function sameCategories(a: ConsentCategories, b: ConsentCategories | null): boolean {
  if (b === null) return false;
  return (Object.keys(a) as (keyof ConsentCategories)[]).every((key) => a[key] === b[key]);
}
